import React, { useRef, useState } from "react";
import { extractApi, formatApiError } from "./api";
import { ExtractionDoc, detectDocKind } from "./data";
import { UploadCloud, FileText, Sparkles, Check, X, AlertTriangle, RefreshCw, Trash2, Plus, FilePlus2 } from "lucide-react";

const num = (n?: number | null) => (n == null || isNaN(Number(n)) ? "" : String(n));

export default function ExtractModal({ onApply, onClose, existingGroups, presetTarget, onApplyCompany }: {
  onApply: (docs: ExtractionDoc[], groupName: string, targetGroupId: string | null) => void;
  onClose: () => void;
  existingGroups: { id: string; name: string; companies: string[] }[];
  presetTarget?: { groupId: string; companyId: string; companyName: string };
  onApplyCompany?: (docs: ExtractionDoc[], groupId: string, companyId: string) => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [err, setErr] = useState("");
  const [docs, setDocs] = useState<ExtractionDoc[] | null>(null);
  const [groupName, setGroupName] = useState("");
  const [mode, setMode] = useState<"new" | "existing">("new");
  const [targetGroupId, setTargetGroupId] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const norm = (s: string) => (s || "").trim().toUpperCase().replace(/^(PT|CV)\.?\s+/i, "").replace(/\s+/g, " ");
  const newCompanyName = (docs || []).find((d) => d.company_name)?.company_name || groupName;
  const targetGroup = existingGroups.find((g) => g.id === targetGroupId);
  const dupCompany = mode === "existing" && !!targetGroup && targetGroup.companies.some((n) => norm(n) === norm(newCompanyName));

  const run = async () => {
    if (!files.length) return;
    setBusy(true); setErr(""); setDocs(null);
    const out: ExtractionDoc[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        setProgress(`Membaca dokumen ${i + 1} dari ${files.length}…`);
        const res = await extractApi.upload(files[i]);
        out.push({ ...(res.extraction || {}), storage_path: res.storage_path, filename: files[i].name });
      }
      out.sort((a, b) => (a.deed_date || "").localeCompare(b.deed_date || ""));
      out.forEach((d, i) => { d.doc_kind = detectDocKind(d, i === 0); });
      setDocs(out);
      if (existingGroups.length && !targetGroupId) setTargetGroupId(existingGroups[0].id);
      const cname = out.find((d) => d.company_name)?.company_name || files[0].name.replace(/\.[^.]+$/, "");
      setGroupName(cname.replace(/^\s*(PT|CV)\.?\s+/i, "").trim() || "Grup Baru");
    } catch (ex: any) {
      setErr(formatApiError(ex?.detail) || "Gagal memproses dokumen.");
    } finally {
      setBusy(false); setProgress("");
    }
  };

  // ---- editor helpers ----
  const upDoc = (di: number, patch: Partial<ExtractionDoc>) =>
    setDocs((p) => p!.map((d, i) => (i === di ? { ...d, ...patch } : d)));
  const upSH = (di: number, si: number, patch: any) =>
    setDocs((p) => p!.map((d, i) => i === di ? { ...d, shareholders: (d.shareholders || []).map((s, j) => j === si ? { ...s, ...patch } : s) } : d));
  const addSH = (di: number) =>
    setDocs((p) => p!.map((d, i) => i === di ? { ...d, shareholders: [...(d.shareholders || []), { name: "", entity_type: "INDIVIDUAL", shares: null, percentage: null }] } : d));
  const delSH = (di: number, si: number) =>
    setDocs((p) => p!.map((d, i) => i === di ? { ...d, shareholders: (d.shareholders || []).filter((_, j) => j !== si) } : d));
  const upKbli = (di: number, ki: number, patch: any) =>
    setDocs((p) => p!.map((d, i) => i === di ? { ...d, kbli: (d.kbli || []).map((k, j) => j === ki ? { ...k, ...patch } : (patch.main ? { ...k, main: false } : k)) } : d));
  const addKbli = (di: number) =>
    setDocs((p) => p!.map((d, i) => i === di ? { ...d, kbli: [...(d.kbli || []), { code: "", title: "", main: false }] } : d));
  const delKbli = (di: number, ki: number) =>
    setDocs((p) => p!.map((d, i) => i === di ? { ...d, kbli: (d.kbli || []).filter((_, j) => j !== ki) } : d));

  return (
    <div className="modal-overlay" data-testid="extract-modal" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 720, maxWidth: "96vw" }}>
        <div className="modal-title serif"><Sparkles size={18} style={{ verticalAlign: "-3px", marginRight: 8 }} />{presetTarget ? `Susulkan dokumen — ${presetTarget.companyName}` : "Ekstraksi Akta AI"}</div>
        <div className="modal-sub">{presetTarget
          ? `Unggah dokumen susulan (NIB, NPWP, akta perubahan, dll.) untuk ${presetTarget.companyName}. AI membaca dokumen lalu melengkapi arsip, KBLI, kedudukan, dan perubahan saham/pengurus PT ini. Anda dapat meninjau & mengoreksi sebelum diterapkan.`
          : "Unggah satu atau beberapa PDF akta (pendirian + perubahan) dari satu PT. AI mengusulkan struktur; Anda dapat mengoreksi lembar saham, nama, dan KBLI sebelum grup dibuat. Tiap dokumen menjadi satu tahap pada timeline."}</div>
        {err && <div className="auth-err" data-testid="extract-error">{err}</div>}

        {!docs && (
          <>
            <div className="ex-drop" onClick={() => inputRef.current?.click()} data-testid="extract-dropzone">
              <input ref={inputRef} type="file" accept="application/pdf,image/*" multiple style={{ display: "none" }}
                onChange={(e) => { setFiles(Array.from(e.target.files || [])); setErr(""); }} data-testid="extract-file-input" />
              {files.length ? (
                <div className="ex-filelist">{files.map((f, i) => <div className="ex-file" key={i}><FileText size={16} /> <span>{f.name}</span></div>)}</div>
              ) : (
                <div className="ex-hint"><UploadCloud size={28} /><div>Klik untuk memilih PDF akta/NIB (bisa beberapa)<br /><small>Maks 25 MB/file · PDF atau gambar</small></div></div>
              )}
            </div>
            <div className="modal-actions">
              <div style={{ flex: 1 }} />
              <button className="btn" onClick={onClose} data-testid="extract-cancel">Batal</button>
              <button className="btn on" onClick={run} disabled={!files.length || busy} data-testid="extract-run">
                {busy ? <><RefreshCw size={15} className="spin" /> {progress || "Memproses…"}</> : <><Sparkles size={15} /> Ekstrak {files.length > 1 ? `${files.length} dokumen` : "dengan AI"}</>}
              </button>
            </div>
          </>
        )}

        {docs && (
          <div className="ex-review" data-testid="extract-review">
            <div className="ex-target" data-testid="extract-target" style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 12, marginBottom: 12 }}>
              {presetTarget ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--secondary)" }} data-testid="extract-preset-banner">
                  <FilePlus2 size={16} />
                  <span>Dokumen akan dilengkapkan ke PT <b>{presetTarget.companyName}</b>. Timeline & data PT ini akan diperbarui otomatis.</span>
                </div>
              ) : (
              <>
              {existingGroups.length > 0 && (
                <div className="ex-mode" style={{ display: "flex", gap: 16, marginBottom: 10 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="radio" name="ex-mode" checked={mode === "new"} onChange={() => setMode("new")} data-testid="extract-mode-new" /> Buat grup baru
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="radio" name="ex-mode" checked={mode === "existing"} onChange={() => setMode("existing")} data-testid="extract-mode-existing" /> Tambahkan ke grup
                  </label>
                </div>
              )}
              {mode === "new" ? (
                <label className="ex-field">
                  <span>Nama grup klien</span>
                  <input value={groupName} onChange={(e) => setGroupName(e.target.value)} data-testid="extract-group-name" placeholder="Nama grup" />
                </label>
              ) : (
                <label className="ex-field">
                  <span>Grup tujuan</span>
                  <select value={targetGroupId} onChange={(e) => setTargetGroupId(e.target.value)} data-testid="extract-target-group" className="filter-select">
                    {existingGroups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                  <small style={{ color: "var(--muted)", marginTop: 4 }}>PT <b>{newCompanyName || "—"}</b> akan ditambahkan ke grup ini; timeline digabung otomatis berdasarkan tanggal akta.</small>
                </label>
              )}
              {dupCompany && (
                <div className="auth-err" data-testid="extract-dup-warning" style={{ marginTop: 8 }}>
                  <AlertTriangle size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                  PT “{newCompanyName}” sudah ada di grup “{targetGroup?.name}”. Ganti nama PT atau pilih grup lain / buat grup baru.
                </div>
              )}
              </>
              )}
            </div>

            {docs.map((d, di) => (
              <div className="ex-doc" key={di} data-testid={`extract-doc-${di}`}>
                <div className="ex-doc-head">
                  <FileText size={15} /> <b>{d.filename || `Dokumen ${di + 1}`}</b>
                  {d.overall_confidence != null && <span className={"ex-conf " + (d.overall_confidence >= 75 ? "ok" : d.overall_confidence >= 50 ? "mid" : "low")} data-testid={`extract-confidence-${di}`}>Keyakinan {d.overall_confidence}%</span>}
                  {(d.overall_confidence ?? 100) < 60 && <span className="ex-warn"><AlertTriangle size={13} /> periksa teliti</span>}
                </div>

                <div className="ex-inline">
                  <label>Perusahaan<input value={d.company_name || ""} onChange={(e) => upDoc(di, { company_name: e.target.value })} data-testid={`ex-company-${di}`} /></label>
                  <label>Domisili<input value={d.domicile || ""} onChange={(e) => upDoc(di, { domicile: e.target.value })} /></label>
                  <label>No. Akta<input value={d.deed_number || ""} onChange={(e) => upDoc(di, { deed_number: e.target.value })} /></label>
                  <label>Tanggal<input type="date" value={d.deed_date || ""} onChange={(e) => upDoc(di, { deed_date: e.target.value })} data-testid={`ex-date-${di}`} /></label>
                  {presetTarget && (
                    <label>Jenis dokumen
                      <select value={d.doc_kind || detectDocKind(d, di === 0)} onChange={(e) => upDoc(di, { doc_kind: e.target.value as ExtractionDoc["doc_kind"] })} data-testid={`ex-doc-kind-${di}`}>
                        <option value="pendirian">Akta Pendirian</option>
                        <option value="perubahan">Akta Perubahan</option>
                        <option value="nib">NIB / Lampiran</option>
                        <option value="npwp">NPWP</option>
                      </select>
                    </label>
                  )}
                </div>
                <div className="ex-inline">
                  <label>Modal dasar<input type="number" value={num(d.modal_dasar)} onChange={(e) => upDoc(di, { modal_dasar: e.target.value === "" ? null : Number(e.target.value) })} /></label>
                  <label>Ditempatkan<input type="number" value={num(d.modal_ditempatkan)} onChange={(e) => upDoc(di, { modal_ditempatkan: e.target.value === "" ? null : Number(e.target.value) })} /></label>
                  <label>Nominal/lembar<input type="number" value={num(d.nominal_per_share)} onChange={(e) => upDoc(di, { nominal_per_share: e.target.value === "" ? null : Number(e.target.value) })} /></label>
                </div>

                <div className="ex-sec">
                  <div className="ex-sec-head"><b>Pemegang saham</b><button className="mini-btn" onClick={() => addSH(di)} data-testid={`ex-add-sh-${di}`}><Plus size={12} /> Tambah</button></div>
                  <table className="ex-table" data-testid={`extract-shareholders-${di}`}>
                    <thead><tr><th>Nama</th><th style={{ width: 96 }}>Jenis</th><th style={{ width: 90 }}>Lembar</th><th style={{ width: 34 }}></th></tr></thead>
                    <tbody>
                      {(d.shareholders || []).map((s, si) => (
                        <tr key={si}>
                          <td><input className="ex-cell" value={s.name || ""} onChange={(e) => upSH(di, si, { name: e.target.value })} data-testid={`ex-sh-name-${di}-${si}`} /></td>
                          <td>
                            <select className="ex-cell" value={s.entity_type || "INDIVIDUAL"} onChange={(e) => upSH(di, si, { entity_type: e.target.value })}>
                              <option value="INDIVIDUAL">Perorangan</option>
                              <option value="COMPANY">Badan</option>
                            </select>
                          </td>
                          <td><input className="ex-cell tnum" type="number" value={num(s.shares)} onChange={(e) => upSH(di, si, { shares: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`ex-sh-shares-${di}-${si}`} /></td>
                          <td><button className="icon-btn" onClick={() => delSH(di, si)} data-testid={`ex-sh-del-${di}-${si}`}><Trash2 size={13} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="ex-sec">
                  <div className="ex-sec-head"><b>KBLI</b><button className="mini-btn" onClick={() => addKbli(di)} data-testid={`ex-add-kbli-${di}`}><Plus size={12} /> Tambah</button></div>
                  {(d.kbli || []).map((k, ki) => (
                    <div className="ex-kbli-row" key={ki}>
                      <input className="ex-cell" style={{ width: 74 }} value={k.code || ""} onChange={(e) => upKbli(di, ki, { code: e.target.value })} placeholder="Kode" data-testid={`ex-kbli-code-${di}-${ki}`} />
                      <input className="ex-cell" value={k.title || ""} onChange={(e) => upKbli(di, ki, { title: e.target.value })} placeholder="Judul KBLI" />
                      <label className="ex-main"><input type="checkbox" checked={!!k.main} onChange={(e) => upKbli(di, ki, { main: e.target.checked })} data-testid={`ex-kbli-main-${di}-${ki}`} /> utama</label>
                      <button className="icon-btn" onClick={() => delKbli(di, ki)}><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="modal-actions" style={{ flexWrap: "wrap", gap: 8 }}>
              <button className="btn" onClick={() => { setDocs(null); setFiles([]); }} data-testid="extract-redo"><X size={14} /> Ulangi</button>
              <div style={{ flex: 1 }} />
              <button className="btn" onClick={onClose} data-testid="extract-close">Tutup</button>
              <button className="btn on" onClick={() => {
                if (presetTarget && onApplyCompany) { onApplyCompany(docs, presetTarget.groupId, presetTarget.companyId); return; }
                onApply(docs, mode === "existing" ? newCompanyName : (groupName.trim() || "Grup Baru"), mode === "existing" ? targetGroupId : null);
              }} disabled={presetTarget ? false : (mode === "new" ? !groupName.trim() : (!targetGroupId || dupCompany))} data-testid="extract-apply">
                <Check size={15} /> {presetTarget ? `Terapkan ke ${presetTarget.companyName}` : mode === "existing" ? `Tambahkan PT ke ${targetGroup?.name || "grup"}` : `Buat grup dari ${docs.length > 1 ? `${docs.length} dokumen` : "dokumen ini"}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
